import { useState, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { useToast } from "../ui/ToastContainer";
import { couponService } from "../../services/couponService";
import { courseService } from "../../services/courseService";
import { packageService } from "../../services/packageService";
import {
  parseApiError,
  formatValidationErrors,
} from "../../utils/errorHandler";

interface Coupon {
  _id?: string;
  code: string;
  description?: string;
  discountType: "percentage" | "fixed";
  discountValue: number;
  maxDiscountAmount?: number;
  minPurchaseAmount: number;
  applicableScope: "all" | "specific" | "category";
  applicableCourses: string[];
  usageLimit: number | null;
  usageLimitPerUser: number;
  startDate: string;
  expiresAt: string;
  isActive: boolean;
  couponType: string;
  targetType?: "course" | "package" | "cart";
  applicablePackages?: string[];
}

interface AddEditCouponDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  coupon?: Coupon | null;
}

interface Course {
  _id: string;
  title: string;
}

interface PackageOption {
  _id: string;
  title: string;
}

export function AddEditCouponDialog({
  isOpen,
  onClose,
  onSuccess,
  coupon,
}: AddEditCouponDialogProps) {
  const isEdit = !!coupon?._id;

  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discountType: "percentage" as "percentage" | "fixed",
    discountValue: 0,
    maxDiscountAmount: 0,
    minPurchaseAmount: 0,
    applicableScope: "all" as "all" | "specific" | "category",
    applicableCourses: [] as string[],
    usageLimit: null as number | null,
    usageLimitPerUser: 1,
    startDate: new Date().toISOString().split("T")[0],
    expiresAt: "",
    isActive: true,
    couponType: "promotional",
    targetType: "course" as "course" | "package" | "cart",
    applicablePackages: [] as string[],
  });

  const [courses, setCourses] = useState<Course[]>([]);
  const [packages, setPackages] = useState<PackageOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchCourses();
      fetchPackages();
      if (coupon) {
        setFormData({
          code: coupon.code || "",
          description: coupon.description || "",
          discountType: coupon.discountType || "percentage",
          discountValue: coupon.discountValue || 0,
          maxDiscountAmount: coupon.maxDiscountAmount || 0,
          minPurchaseAmount: coupon.minPurchaseAmount || 0,
          applicableScope: coupon.applicableScope || "all",
          applicableCourses: coupon.applicableCourses || [],
          usageLimit: coupon.usageLimit,
          usageLimitPerUser: coupon.usageLimitPerUser || 1,
          startDate: coupon.startDate
            ? new Date(coupon.startDate).toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0],
          expiresAt: (coupon.expiresAt
            ? new Date(coupon.expiresAt).toISOString().split("T")[0]
            : "") as string,
          isActive: coupon.isActive ?? true,
          couponType: coupon.couponType || "promotional",
          targetType: (coupon.targetType || "course") as
            | "course"
            | "package"
            | "cart",
          applicablePackages: coupon.applicablePackages || [],
        });
      } else {
        // Reset form for adding
        setFormData({
          code: "",
          description: "",
          discountType: "percentage",
          discountValue: 0,
          maxDiscountAmount: 0,
          minPurchaseAmount: 0,
          applicableScope: "all",
          applicableCourses: [],
          usageLimit: null,
          usageLimitPerUser: 1,
          startDate: new Date().toISOString().split("T")[0],
          expiresAt: "",
          isActive: true,
          couponType: "promotional",
          targetType: "course",
          applicablePackages: [],
        });
      }
      setError(null);
      setValidationErrors({});
    }
  }, [isOpen, coupon]);

  useEffect(() => {
    // Reset scope and selection arrays on target toggle so stale data does not bleed across.
    setFormData((prev) => ({
      ...prev,
      applicableScope: "all",
      applicableCourses: [],
      applicablePackages: [],
    }));
  }, [formData.targetType]);

  const fetchCourses = async () => {
    try {
      const response = await courseService.getAllCourses({ limit: 100 });
      setCourses(response.data?.courses || []);
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await packageService.getAdminPackages({ limit: 100 });
      const list = (response.data?.packages ||
        (response as any).packages ||
        []) as PackageOption[];
      setPackages(list);
    } catch (err) {
      console.error("Failed to fetch packages:", err);
      setPackages([]);
    }
  };

  const generateCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      // Map form data to API expected format.
      // Cart coupons ignore applicableCourses/ applicablePackages and
      // the scope is forced to 'all'.
      const isCart = formData.targetType === "cart";

      const payload: any = {
        code: formData.code,
        description: formData.description,
        discountValue: Number(formData.discountValue),
        discountType: formData.discountType,
        expiresAt: formData.expiresAt,
        usageLimit: formData.usageLimit ? Number(formData.usageLimit) : null,
        applicableScope: isCart ? "all" : formData.applicableScope,
        applicableCourses:
          !isCart && formData.applicableScope === "specific"
            ? formData.applicableCourses
            : [],
        maxDiscountAmount: formData.maxDiscountAmount
          ? Number(formData.maxDiscountAmount)
          : undefined,
        minPurchaseAmount: Number(formData.minPurchaseAmount),
        usageLimitPerUser: Number(formData.usageLimitPerUser),
        startDate: formData.startDate,
        isActive: formData.isActive,
        couponType: formData.couponType,
        targetType: formData.targetType,
        applicablePackages:
          formData.targetType === "package" &&
          formData.applicableScope === "specific"
            ? formData.applicablePackages
            : [],
      };

      if (isEdit && coupon?._id) {
        await couponService.updateCoupon(coupon._id, payload);
        showSuccess("Coupon updated successfully", "Success");
      } else {
        await couponService.createCoupon(payload);
        showSuccess("Coupon created successfully", "Success");
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      const apiError = parseApiError(err);

      if (apiError.errors && Object.keys(apiError.errors).length > 0) {
        setValidationErrors(apiError.errors);
        setError("Please fix the validation errors below.");
      } else {
        setError(apiError.message);
      }

      showError(apiError.message, isEdit ? "Update Failed" : "Create Failed");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit Coupon" : "Create New Coupon"}
      className="max-w-2xl"
    >
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-red-600">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={16} />
                <span className="text-sm font-medium">{error}</span>
              </div>
              {Object.keys(validationErrors).length > 0 && (
                <ul className="mt-2 ml-6 list-disc space-y-1 text-xs">
                  {formatValidationErrors(validationErrors).map((err, idx) => (
                    <li key={idx}>
                      <span className="font-medium">{err.field}:</span>{" "}
                      {err.messages.join(", ")}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Code and Generate */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coupon Code *
              </label>
              <Input
                value={formData.code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    code: e.target.value.toUpperCase(),
                  })
                }
                placeholder="e.g., SUMMER2025"
                required
                className="font-mono uppercase"
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                variant="outline"
                onClick={generateCode}
                className="w-full"
              >
                Generate
              </Button>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              rows={2}
              placeholder="Internal description for this coupon"
            />
          </div>

          {/* Discount Type and Value */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Type *
              </label>
              <select
                value={formData.discountType}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discountType: e.target.value as "percentage" | "fixed",
                  })
                }
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount ($)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Value *
              </label>
              <Input
                type="number"
                value={formData.discountValue}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discountValue: Number(e.target.value),
                  })
                }
                min={0}
                max={formData.discountType === "percentage" ? 100 : undefined}
                required
              />
            </div>
          </div>

          {/* Max Discount and Min Purchase */}
          <div className="grid grid-cols-2 gap-4">
            {formData.discountType === "percentage" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Discount Amount ($)
                </label>
                <Input
                  type="number"
                  value={formData.maxDiscountAmount || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxDiscountAmount: Number(e.target.value),
                    })
                  }
                  min={0}
                  placeholder="No limit"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Purchase Amount ($)
              </label>
              <Input
                type="number"
                value={formData.minPurchaseAmount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    minPurchaseAmount: Number(e.target.value),
                  })
                }
                min={0}
              />
            </div>
          </div>

          {/* Coupon Target */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Coupon Target
            </label>
            <div className="grid grid-cols-3 gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
              <button
                type="button"
                onClick={() =>
                  setFormData({ ...formData, targetType: "course" })
                }
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  formData.targetType === "course"
                    ? "bg-white text-amber-700 shadow-sm border border-amber-200"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Courses
              </button>
              <button
                type="button"
                onClick={() =>
                  setFormData({ ...formData, targetType: "package" })
                }
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  formData.targetType === "package"
                    ? "bg-white text-amber-700 shadow-sm border border-amber-200"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Packages
              </button>
              <button
                type="button"
                onClick={() =>
                  setFormData({ ...formData, targetType: "cart" })
                }
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  formData.targetType === "cart"
                    ? "bg-white text-amber-700 shadow-sm border border-amber-200"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Cart
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Choose whether this coupon targets courses, packages, or the
              cart subtotal.
            </p>
          </div>

          {formData.targetType === "course" && (
            <>
              {/* Applicable Scope */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Applicable To
                </label>
                <select
                  value={formData.applicableScope}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      applicableScope: e.target.value as
                        | "all"
                        | "specific"
                        | "category",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                >
                  <option value="all">All Courses</option>
                  <option value="specific">Specific Courses</option>
                </select>
              </div>

              {/* Course Selection (if specific) */}
              {formData.applicableScope === "specific" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Courses
                  </label>
                  <div className="border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                    {courses.map((course) => (
                      <label
                        key={course._id}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.applicableCourses.includes(course._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                applicableCourses: [
                                  ...formData.applicableCourses,
                                  course._id,
                                ],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                applicableCourses:
                                  formData.applicableCourses.filter(
                                    (id) => id !== course._id
                                  ),
                              });
                            }
                          }}
                          className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm">{course.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {formData.targetType === "package" && (
            <section className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
              <header>
                <h3 className="text-sm font-semibold text-gray-700">
                  Package Settings
                </h3>
                <p className="text-xs text-gray-500">
                  Configure which packages this coupon applies to.
                </p>
              </header>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Applicable To
                </label>
                <select
                  value={formData.applicableScope}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      applicableScope: e.target.value as "all" | "specific",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                >
                  <option value="all">All Packages</option>
                  <option value="specific">Specific Packages</option>
                </select>
              </div>

              {formData.applicableScope === "specific" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Packages
                  </label>
                  <div className="border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2 bg-white">
                    {packages.map((pkg) => (
                      <label
                        key={pkg._id}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.applicablePackages.includes(pkg._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                applicablePackages: [
                                  ...formData.applicablePackages,
                                  pkg._id,
                                ],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                applicablePackages:
                                  formData.applicablePackages.filter(
                                    (id) => id !== pkg._id,
                                  ),
                              });
                            }
                          }}
                          className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm">{pkg.title}</span>
                      </label>
                    ))}
                    {packages.length === 0 && (
                      <p className="text-xs text-gray-500">
                        No packages available.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

          {formData.targetType === "cart" && (
            <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
              <header>
                <h3 className="text-sm font-semibold text-amber-800">
                  Cart Coupon
                </h3>
                <p className="text-xs text-amber-700">
                  Cart coupons apply to the cart subtotal at checkout. No
                  specific course or package selection is required.
                </p>
              </header>
              <ul className="text-xs text-amber-800 list-disc pl-4 space-y-1">
                <li>Applicable scope is locked to <strong>All</strong>.</li>
                <li>Course and package selection fields are not used.</li>
              </ul>
            </section>
          )}

          {/* Usage Limits */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Usage Limit
              </label>
              <Input
                type="number"
                value={formData.usageLimit || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    usageLimit: e.target.value ? Number(e.target.value) : null,
                  })
                }
                min={1}
                placeholder="Unlimited"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Per User Limit
              </label>
              <Input
                type="number"
                value={formData.usageLimitPerUser}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    usageLimitPerUser: Number(e.target.value),
                  })
                }
                min={1}
                required
              />
            </div>
          </div>

          {/* Validity Period */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date *
              </label>
              <Input
                type="date"
                value={formData.expiresAt}
                onChange={(e) =>
                  setFormData({ ...formData, expiresAt: e.target.value })
                }
                required
              />
            </div>
          </div>

          {/* Coupon Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Coupon Type
            </label>
            <select
              value={formData.couponType}
              onChange={(e) =>
                setFormData({ ...formData, couponType: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            >
              <option value="promotional">Promotional</option>
              <option value="referral">Referral</option>
              <option value="seasonal">Seasonal</option>
              <option value="bundle">Bundle</option>
              <option value="early_bird">Early Bird</option>
              <option value="special">Special</option>
            </select>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
              className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700">
              Coupon is active
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading
              ? "Saving..."
              : isEdit
              ? "Update Coupon"
              : "Create Coupon"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
